<template>
  <div class="flex">
    <spinner :visible.sync="this.loading" :overlay="true"></spinner>
    <div class="content">
      <div class="consent-row" id="consent-filters">
        <div class="filter-container flex">
          <span class="title-filter-wrapper">
            <input
              type="text"
              class="title-filter"
              :placeholder="i18nJS.title_search"
              v-model="filters.title"
              @keyup.enter="confirmTitleFilter"
              @keyup.esc="undoTitleFilter"
            />
          </span>
          <span class="consent-status consent-status-filter-wrapper">
            <v-select2
              v-model="filters.consentStatus"
              class="input-medium"
              :options-list="optionsList"
              id="select2-consent-status"
              :data-allow-clear="true"
              :data-placeholder="`${i18nJS.search_by} `"
              @input="confirmConsentStatusFilter"
            >
            </v-select2>
          </span>
          <div class="signer signer-filter-wrapper">
            <input
              type="text"
              class="signer-filter"
              :placeholder="i18nJS.consents.index.signer"
              v-model="filters.signer"
              @keyup.enter="confirmSignerFilter"
              @keyup.esc="undoSignerFilter"
            />
          </div>
        </div>
      </div>
      <div class="consents-container flex-all-centered">
        <div class="consent-row header">
          <div class="buttons"></div>
          <div style="width: 25%">{{ i18nJS.title }}</div>
          <div class="consent_status"></div>
          <div class="sections">{{ i18nJS.consent_templates.index.sections }}</div>
          <div class="signer">{{ i18nJS.consents.index.signer }}</div>
          <div class="collector">{{ i18nJS.consents.index.collector }}</div>
        </div>
        <div
          :ref="`c-${consent.id}`"
          :id="`c-${consent.id}`"
          class="consent-row"
          :class="{ expired: consent.expired }"
          v-for="consent in notDeletedConsent"
          v-bind:key="consent.id"
        >
          <div class="buttons">
            <a class="fa-light primary-color fa-eye" v-if="canShow(consent.id)" :href="showC(consent.id)" />
            <i class="fa-light primary-color fa-trash" v-if="canDestroy(consent.id)" @click="destroyC(consent.id)" />
            <!-- <a class="fa-light primary-color fa-pencil" v-if="canEdit(consent.id)" :href="editC(consent.id)" /> -->
            <i class="fa-light primary-color fa-print" v-if="canPrint(consent.id) && consent.id" @click="printC(consent)" />
            <a class="btn btn-primary" style="margin-top: 10px" v-if="consent.id == null" :href="newC(consent.consent_template.id)">{{
              i18nJS.consents.index.administer
            }}</a>
          </div>
          <div class="title" style="margin: 12px 0px 0px 0px; font-size: 1.8rem">{{ consent.title }}</div>
          <div class="consent_status">
            <stepper
              :step="consent.consent_status"
              :title_prefix="'consent_states'"
              :steps="[
                { name: 'given', i: 1, ico: 'file-contract', consent: consent },
                { name: 'aquired', i: 2, ico: 'file-signature', consent: consent },
                { name: 'accepted', i: 3, ico: 'check', consent: consent },
              ]"
            />
          </div>
          <div class="sections" style="margin-top: 10px">
            <div class="header" :class="{ present: consent.consent_template.header && consent.consent_template.header.length }"></div>
            <div class="s-count">{{ consent.consent_template.consent_template_sections_count }}</div>
            <div class="footer" :class="{ present: consent.consent_template.footer && consent.consent_template.footer.length }"></div>
          </div>
          <div class="signer" style="margin: 12px 0px 0px 0px">{{ consent.signer }}</div>
          <div class="collector">
            <user-media
              :datetime="consent.date"
              :fullname="consent.modifier_full_name"
              :img="consent.modifier_avatar"
              :job="consent.modifier_job"
              v-if="consent.id"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { Route } from '../../route';
import spinner from '../spinner';
import stepper from '../stepper';
import userMedia from '../userMedia';
import vSelect2 from '../vSelect2';
import apiConnector from '@/epersonam/apiConnector';
import { print } from '../../mixins/print';
import { consent_variables } from './consentsVariables';
import { consent_common } from './common';

export default {
  name: 'consents',
  data() {
    return {
      consents: [],
      editing: false,
      loading: true,
      filters: {
        title: null,
        consentStatus: null,
        signer: null,
      },
      consent: {},
      timeout: false,
      access_denied: false,
      stopPaging: false,
      page: 1,
    };
  },
  mixins: [print, consent_variables, consent_common],
  props: {
    guest_period_data_set_id: { type: Number },
    users_business_unit_id: { type: Number },
    can: {
      type: Object,
      default: () => {
        return { resource: 'Consent', permit: [] };
      },
    },
    page_size: { type: Number, default: 10 },
  },
  components: {
    spinner,
    stepper,
    userMedia,
    vSelect2,
  },
  computed: {
    resolvedConsentsUrl() {
      if (this.guest_period_data_set_id) {
        return Route.GPDS_CONSENTS.replace('__GPDS_ID__', this.guest_period_data_set_id);
      } else if (this.users_business_unit_id) {
        return Route.USERS_BU_CONSENTS.replace('__USERS_BU_ID__', this.users_business_unit_id);
      } else {
        return '';
      }
    },
    notDeletedConsent() {
      return this.consents.filter((f) => {
        return !f.removed;
      });
    },
    i18nJS() {
      return window.i18nJS;
    },
    optionsList() {
      return [
        [i18nJS.all, null],
        [i18nJS.consent_states.given.undone, 0],
        [i18nJS.consent_states.given.done, 1],
        [i18nJS.consent_states.aquired.done, 2],
      ];
    },
  },
  created() {
    window.addEventListener('scroll', this.handleScroll);
  },
  mounted() {
    this.loadConsents(true);
  },
  methods: {
    canShow(id) {
      return id && this.can[id]?.permit.includes('show');
    },
    canDestroy(id) {
      return id && this.can[id]?.permit && this.can[id]?.permit.includes('destroy');
    },
    // canEdit(id) {
    //   return id && this.can[id].permit && this.can[id].permit.includes('edit')
    // },
    canPrint(id) {
      return !id || (this.can[id]?.permit && this.can[id]?.permit.includes('print'));
    },
    showC(id) {
      if (id) {
        return `${this.resolvedConsentsUrl}/${id}`;
      }
    },
    destroyC(id) {
      if (this.canDestroy(id) && confirm(i18nJS.javascript.alert.confirmation)) {
        eventBus.$emit('updatingC', { id: id });
        axios({
          method: 'delete',
          url: `${this.resolvedConsentsUrl}/${id}.json`,
          headers: {
            'Content-type': 'application/json',
            'X-CSRF-Token': $.rails.csrfToken(),
          },
        })
          .then((res) => {
            this.$refs[`c-${id}`][0].classList.add('removed-item');
            setTimeout(() => {
              eventBus.$emit('removedC', { id: id, success: true });
              this.$refs[`c-${id}`][0].style.display = 'none';
              let removedItemIdx = 0;
              for (let c in this.consents) {
                if (this.consents[c].id == id) {
                  break;
                }
                removedItemIdx += 1;
              }
              this.consents.splice(removedItemIdx, 1);
            }, 800);
          })
          .catch((err) => {
            eventBus.$emit('updatedCT', { id: id, success: false });
            this.loading = false;
            var msg = `${i18nJS.errors.critical_error}: ${err.message}`;
            if (err.response.status === 401) {
              msg = i18nJS.access_denied;
            }
            Utils.appendToFlash('error', msg);
          });
      }
    },
    editC(id) {
      if (id) {
        return `${this.resolvedConsentsUrl}/${id}/edit`;
      }
    },
    newC(consent_template_id) {
      return `${this.resolvedConsentsUrl}/new?consent_template_id=${consent_template_id}`;
    },
    async loadDetails(id) {
      const response = await axios({
        method: 'post',
        url: `${this.resolvedConsentsUrl}/detail.json`,
        headers: {
          'Content-type': 'application/json',
          'X-CSRF-Token': $.rails.csrfToken(),
        },
        data: {
          consent_id: id,
        },
      });
      return response.data;
    },
    async printC(consent) {
      this.loading = true;
      const response = await this.loadDetails(consent.id);
      const { requirements, variables } = response;
      this.consent = {};
      this.consent = { ...consent };
      const translates = await this.CV_resolveVariables(requirements, variables);
      await this.CV_setVariables(translates);
      const { consent_template } = this.consent;
      const { responses } = consent;

      apiConnector()
        .post('/it/reports/generate_report', {
          data: { responses, ...consent_template, signatureInfo: this.COM_signatureInfo() },
          type: 'pdf',
          frontend_pdf_class: 'ConsentTemplatePdf',
          skip_page_creation: 'false',
        })
        .then(() => {
          this.loading = false;
        })
        .catch((err) => {
          var msg = `${i18nJS.errors.critical_error}: ${err.message} - ${err.response.data}`;
          if (err.response.status === 401) {
            msg = i18nJS.access_denied;
          }
          Utils.appendToFlash('error', msg);
          this.loading = false;
        });
    },
    handleScroll() {
      let scrollHeight = window.scrollY;
      let maxHeight = window.document.body.scrollHeight - window.document.documentElement.clientHeight;

      if (!this.loading && !this.stopPaging && scrollHeight >= maxHeight - 200) {
        this.page = this.page + 1;
        this.loadConsents(false);
      }
    },
    confirmTitleFilter() {
      this.loadConsents().catch((res) => {
        this.filters.title = '';
      });
    },
    confirmSignerFilter() {
      this.loadConsents().catch((res) => {
        this.filters.signer = null;
      });
    },
    confirmConsentStatusFilter() {
      this.loadConsents().catch((res) => {
        this.filters.consentStatus = null;
      });
    },
    undoTitleFilter() {},
    clearFilters() {
      this.filters.title = null;
      this.filters.consentStatus = null;
      this.filters.signer = null;
      this.loadConsents();
    },
    clearTitleFilter() {
      if (this.filters.title && this.filters.title.length) {
        this.filters.title = null;
        this.loadConsents();
      }
    },
    clearSignerFilter() {
      if (this.filters.signer && this.filters.signer.length) {
        this.filters.signer = null;
        this.loadConsents();
      }
    },
    urlForLoadConsents() {
      if (this.guest_period_data_set_id) {
        return Route.GPDS_CONSENTS.replace('__GPDS_ID__', this.guest_period_data_set_id);
      } else if (this.users_business_unit_id) {
        return Route.USERS_BU_CONSENTS.replace('__USERS_BU_ID__', this.users_business_unit_id);
      }
    },
    loadConsents(clear = true) {
      this.error = false;
      this.timeout = false;
      this.access_denied = false;
      let url = this.urlForLoadConsents();
      url = this.page && this.page > 1 ? `${url}.json?page=${this.page}` : `${url}.json?`;

      if (this.filters.title) {
        url = url + `title=${this.filters.title}`;
      }
      if (this.filters.signer) {
        url = url + `&signer=${this.filters.signer}`;
      }
      if (this.page && this.page > 1) {
        url = url + `&page=${this.page}`;
      }
      if (this.filters.consentStatus) {
        url = url + `&consent_status=${this.filters.consentStatus}`;
      }

      return new Promise((resolve, reject) => {
        this.loading = true;
        axios
          .get(url)
          .then((res) => {
            this.loading = false;
            if (res.data.length < this.page_size) {
              this.stopPaging = true;
            }
            if (clear) {
              this.consents.splice(0, this.consents.length);
            }
            res.data.forEach((ele) => this.consents.push(ele));
            resolve();
          })
          .catch((error) => {
            this.loading = false;
            if (error.response && (error.response.status == 504 || error.response.status == 408)) {
              this.timeout = true;
            } else if (error.response && (error.response.status == 401 || error.response.status == 403)) {
              this.access_denied = true;
            } else {
              this.error = true;
            }
            reject();
          });
      });
    },
  },
};
</script>

<style lang="scss" scoped>
div.removed-item {
  animation: removed-item-animation 0.6s cubic-bezier(0.55, -0.04, 0.91, 0.94) forwards;
}

@keyframes removed-item-animation {
  from {
    opacity: 1;
    transform: scale(1);
  }

  to {
    opacity: 0;
    transform: scale(0);
  }
}
.reset {
  padding: 1em;
  top: -4px;
  position: relative;
  &::before {
    content: '\f0e2';
    font-family: 'Font Awesome 6 Pro';
    margin-right: 0.5em;
  }
}
.sections {
  width: 5%;
  display: flex;
  flex-direction: column;
  & > .header,
  & > .footer {
    height: 5px;
    width: 30px;
    background-color: #ccc;
    &.present {
      background-color: var(--adv-primary-color);
    }
  }
  & > .s-count {
    padding-left: 20px;
    position: relative;
    &:after {
      position: absolute;
      left: 2px;
      top: 0;
      content: '\f0ae';
      font-family: 'Font Awesome 6 Pro';
      color: #ccc;
      margin-right: 0.5em;
      font-size: 1em;
    }
  }
}
.title,
.collector,
.target {
  margin: auto 1em;
}
.title {
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 16px;
  font-weight: 400 !important;
  color: #000000;
}
.content {
  display: flex;
  flex-flow: column;
  width: 100%;
}
.consents-container {
  flex-wrap: wrap;
  width: 100%;
  margin-top: 1em;

  & > .consent-row {
    display: flex;
    width: 100%;
    &:nth-child(odd) {
      background-color: #f9f9f9;
    }
    &.header {
      background-color: #c5eced;
      color: var(--adv-primary-color);
      font-family: Asap-SemiBold;
      padding: 7px 0;
    }
    &.expired {
      background-color: #fcc;
    }
  }
}
.buttons {
  width: 15%;
  margin-left: auto;
}
.title {
  width: 25%;
}
.consent_status {
  width: 15%;
}
.sections {
  width: 5%;
}
.signer {
  width: 15%;
}
.collector {
  width: 20%;
}

.filter-container {
  display: flex;
  flex-direction: column;

  @media screen and (min-width: 768px) {
    flex-direction: row;
    justify-content: flex-start;
  }

  > span,
  div {
    margin-right: 20px;
  }

  .title-filter {
    max-width: 200px;
  }

  .consent-status {
    margin-bottom: 10px;
  }
}

.section {
  & > input:first-child {
    margin-left: 2.1em;
  }
  & > i {
    margin: auto 0.5em;
  }
}
.buttons {
  & > i,
  .pulsante_comando,
  a {
    cursor: pointer;
    padding: 1em 0.5em;
  }
}

.pulsante_comando.disabled {
  cursor: not-allowed;
  color: #bbb;
}

.no-res {
  margin: 2em;
  width: 90%;
}
.title-filter-wrapper,
.signer-filter-wrapper {
  display: flex;
  position: relative;

  input {
    width: 100%;
  }

  & > .clear-title-filter,
  & > .clear-signer-filter {
    position: relative;
    left: -1.8em;
    padding-top: 0.6em;
    color: #6c6c6c;
    cursor: pointer;
  }
}
</style>
